import React, { useRef, useEffect } from "react";
import QRCodeLib from "qrcode";
import { toPng } from "html-to-image";
import jsPDF from "jspdf";
import { formatDate, formatCurrency } from "../../utils/helpers";

const CARD_W = 720;
const CARD_H = 452; // 86 × 54 mm (standard print size)

const QR_SIZE = 105;

export default function WarrantyCard({ product, onClose }) {
  const cardRef = useRef(null);
  const qrCanvasRef = useRef(null);
  // Normalize to https — old products stored http:// URLs; Render always serves https
  const toHttps = (url) => url?.replace(/^http:\/\//, "https://");
  const coverImage = toHttps(product.images?.[0]?.url);

  useEffect(() => {
    if (!qrCanvasRef.current) return;
    const url = `${window.location.origin}/warranty/${product.warrantyId}`;
    QRCodeLib.toCanvas(qrCanvasRef.current, url, {
      width: QR_SIZE, margin: 1, color: { dark: "#000000", light: "#ffffff" },
    });
  }, [product.warrantyId]);

  const toCanvas = async (dataUrl) => {
    const img = new Image();
    img.src = dataUrl;
    await img.decode();
    const cv = document.createElement("canvas");
    cv.width  = img.naturalWidth;
    cv.height = img.naturalHeight;
    cv.getContext("2d").drawImage(img, 0, 0);
    return cv;
  };

  const capture = async () => {
    await new Promise((res) => setTimeout(res, 300));

    const opts = { pixelRatio: 3, backgroundColor: "#0a0a0a", cacheBust: true, skipFonts: true };

    try {
      return await toCanvas(await toPng(cardRef.current, opts));
    } catch {
      // Image fetch failed (CORS/mixed-content) — capture without external images, then stamp them back
      const filter = (node) => node.tagName !== "IMG";
      const base = await toCanvas(await toPng(cardRef.current, { ...opts, filter }));

      const imgs = cardRef.current.querySelectorAll("img");
      const cardRect = cardRef.current.getBoundingClientRect();
      const s = 3;
      await Promise.all([...imgs].map((el) => new Promise((res) => {
        const r = el.getBoundingClientRect();
        const i = new Image();
        i.crossOrigin = "anonymous";
        i.onload = () => {
          base.getContext("2d").drawImage(i, (r.left - cardRect.left) * s, (r.top - cardRect.top) * s, r.width * s, r.height * s);
          res();
        };
        i.onerror = res;
        i.src = toHttps(el.src);
      })));

      return base;
    }
  };

  const downloadPDF = async () => {
    const canvas = await capture();
    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF({ orientation: "landscape", unit: "mm", format: [86, 54] });
    pdf.addImage(imgData, "PNG", 0, 0, 86, 54);
    pdf.save(`${product.warrantyId}-makalu-agro-card.pdf`);
  };

  const downloadImage = async () => {
    const canvas = await capture();
    const link = document.createElement("a");
    link.download = `${product.warrantyId}-makalu-agro-card.png`;
    link.href = canvas.toDataURL("image/png");
    link.click();
  };

  // Auto-shrink font for long product names
  const nameLen = product.productName?.length || 0;
  const nameFontSize = nameLen > 22 ? 13 : nameLen > 16 ? 15 : 17;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto" style={{ background: "rgba(0,0,0,0.85)" }}>
      <div className="rounded-2xl shadow-2xl w-full max-w-3xl" style={{ background: "#141414", border: "1px solid rgba(200,150,12,0.3)" }}>

        {/* Action Bar */}
        <div className="flex items-center justify-between px-5 py-3" style={{ borderBottom: "1px solid rgba(200,150,12,0.2)" }}>
          <h2 className="font-bold" style={{ color: "#FFD700", fontFamily: "serif", letterSpacing: "0.05em" }}>Authenticity Certificate</h2>
          <div className="flex gap-2">
            <button onClick={downloadImage} className="btn-secondary text-xs px-3 py-1.5">PNG</button>
            <button onClick={downloadPDF} className="btn-primary text-xs px-3 py-1.5">PDF</button>
            <button onClick={onClose} className="btn-danger text-xs px-3 py-1.5">Close</button>
          </div>
        </div>

        {/* Card Preview */}
        <div className="flex justify-center p-6" id="warranty-card">
          <div
            ref={cardRef}
            style={{
              width: CARD_W, height: CARD_H,
              background: "linear-gradient(135deg, #0a0a0a 0%, #181818 40%, #111 100%)",
              border: "2px solid #C8960C",
              borderRadius: 10, overflow: "hidden",
              position: "relative",
              fontFamily: "Arial, sans-serif",
              boxSizing: "border-box",
            }}
          >
            {/* Gold corner accents */}
            {[["10px 0 0 0","top:0;left:0"],["0 10px 0 0","top:0;right:0"],["0 0 0 10px","bottom:0;left:0"],["0 0 10px 0","bottom:0;right:0"]].map(([r, pos], i) => (
              <div key={i} style={{ position:"absolute", width:40, height:40, borderRadius:r, zIndex:2, ...Object.fromEntries(pos.split(";").map(p => p.split(":"))), ...(i===0?{borderTop:"3px solid #FFD700",borderLeft:"3px solid #FFD700"}:i===1?{borderTop:"3px solid #FFD700",borderRight:"3px solid #FFD700"}:i===2?{borderBottom:"3px solid #FFD700",borderLeft:"3px solid #FFD700"}:{borderBottom:"3px solid #FFD700",borderRight:"3px solid #FFD700"}) }} />
            ))}

            {/* Gold top strip */}
            <div style={{ height:4, background:"linear-gradient(90deg, transparent, #8A6000, #FFD700, #C8960C, #FFD700, #8A6000, transparent)" }} />

            <div style={{ display:"flex", height:"calc(100% - 4px)", padding:"10px 14px 8px 14px", gap:10 }}>

              {/* LEFT COLUMN */}
              <div style={{ display:"flex", flexDirection:"column", justifyContent:"space-between", width:155, flexShrink:0 }}>
                <div>
                  <img src="/makalu-logo.svg" alt="Makalu" style={{ height:50, width:"auto" }} />
                  <div style={{ fontSize:10, color:"#C8960C", letterSpacing:"0.08em", fontWeight:700, marginTop:4, lineHeight:1.3 }}>Makalu Agro and Trade</div>
                </div>

                {coverImage ? (
                  <div style={{ flex:1, display:"flex", alignItems:"center", justifyContent:"center", marginTop:6 }}>
                    <img src={toHttps(coverImage)} alt="Rudraksha" crossOrigin="anonymous"
                      style={{ maxWidth:148, maxHeight:300, objectFit:"contain", borderRadius:8, border:"1px solid rgba(200,150,12,0.5)" }} />
                  </div>
                ) : (
                  <div style={{ flex:1, display:"flex", alignItems:"center", justifyContent:"center", marginTop:6 }}>
                    <div style={{ width:130, height:200, borderRadius:8, background:"rgba(200,150,12,0.04)", border:"1px dashed rgba(200,150,12,0.3)", display:"flex", alignItems:"center", justifyContent:"center" }}>
                      <span style={{ fontSize:9, color:"#5a4a30" }}>No Image</span>
                    </div>
                  </div>
                )}

                <div style={{ paddingTop:5, borderTop:"1px solid rgba(200,150,12,0.3)" }}>
                  <div style={{ fontSize:9, color:"#8A6000", letterSpacing:"0.12em" }}>ORIGIN</div>
                  <div style={{ fontSize:18, fontWeight:900, color:"#FFD700", letterSpacing:"0.08em", lineHeight:1 }}>NEPAL</div>
                </div>
              </div>

              {/* DIVIDER */}
              <div style={{ width:1, background:"linear-gradient(180deg, transparent, #C8960C, #FFD700, #C8960C, transparent)", opacity:0.5 }} />

              {/* CENTER COLUMN */}
              <div style={{ flex:1, display:"flex", flexDirection:"column", justifyContent:"space-between", minWidth:0 }}>
                <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between" }}>
                  <div style={{ minWidth:0, flex:1, paddingRight:8 }}>
                    <div style={{ fontSize:9, color:"#8A6000", letterSpacing:"0.15em", textTransform:"uppercase" }}>Authenticity Certificate</div>
                    <div style={{ fontSize:nameFontSize, fontWeight:900, color:"#FFD700", letterSpacing:"0.02em", lineHeight:1.2, wordBreak:"break-word" }}>
                      {product.productName}
                    </div>
                    <div style={{ fontSize:14, color:"#C8960C", fontWeight:700, marginTop:2 }}>{formatCurrency(product.productPrice)}</div>
                  </div>
                  <div style={{ textAlign:"right", flexShrink:0 }}>
                    <div style={{ fontSize:9, color:"#8A6000", letterSpacing:"0.08em" }}>CARD UNIQUE ID</div>
                    <div style={{ fontSize:11, fontFamily:"monospace", color:"#FFD700", fontWeight:700 }}>{product.warrantyId}</div>
                  </div>
                </div>

                <div style={{ height:1, background:"linear-gradient(90deg, transparent, #C8960C, transparent)" }} />

                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"8px 12px" }}>
                  <div>
                    <div style={{ fontSize:9, color:"#8A6000", letterSpacing:"0.1em", textTransform:"uppercase" }}>Customer Name</div>
                    <div style={{ fontSize:14, color:"#f5f0e8", fontWeight:700, lineHeight:1.2 }}>{product.customerName}</div>
                  </div>
                  <div>
                    <div style={{ fontSize:9, color:"#8A6000", letterSpacing:"0.1em", textTransform:"uppercase" }}>Phone Number</div>
                    <div style={{ fontSize:15, color:"#f5f0e8", fontWeight:700 }}>{product.phoneNumber}</div>
                  </div>
                  <div>
                    <div style={{ fontSize:9, color:"#8A6000", letterSpacing:"0.1em", textTransform:"uppercase" }}>Purchase Date</div>
                    <div style={{ fontSize:13, color:"#f5f0e8", fontWeight:600 }}>{formatDate(product.purchaseDate)}</div>
                  </div>
                  {product.rudrakshaSize ? (
                    <div>
                      <div style={{ fontSize:9, color:"#8A6000", letterSpacing:"0.1em", textTransform:"uppercase" }}>Size / Mukhi</div>
                      <div style={{ fontSize:14, color:"#FFD700", fontWeight:700 }}>{product.rudrakshaSize}</div>
                    </div>
                  ) : (
                    <div>
                      <div style={{ fontSize:9, color:"#8A6000", letterSpacing:"0.1em", textTransform:"uppercase" }}>Guarantee</div>
                      <div style={{ fontSize:14, color:"#FFD700", fontWeight:700 }}>Authentic</div>
                    </div>
                  )}
                </div>

                <div style={{ height:1, background:"linear-gradient(90deg, transparent, #C8960C, transparent)" }} />

                <div>
                  <div style={{ fontSize:9, color:"#8A6000", letterSpacing:"0.1em", textTransform:"uppercase", marginBottom:3 }}>Address</div>
                  <div style={{ fontSize:12, color:"#d4c090", lineHeight:1.5 }}>{product.address}</div>
                </div>
              </div>

              {/* DIVIDER */}
              <div style={{ width:1, background:"linear-gradient(180deg, transparent, #C8960C, #FFD700, #C8960C, transparent)", opacity:0.5 }} />

              {/* RIGHT COLUMN — QR */}
              <div style={{ width:115, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"space-between", flexShrink:0 }}>
                <div style={{ textAlign:"center" }}>
                  <div style={{ fontSize:9, color:"#C8960C", letterSpacing:"0.12em", marginBottom:4, fontWeight:700 }}>SCAN TO VERIFY</div>
                  <div style={{ background:"#fff", padding:4, borderRadius:6, display:"inline-block", border:"2px solid #C8960C" }}>
                    <canvas ref={qrCanvasRef} style={{ display:"block", width:QR_SIZE, height:QR_SIZE }} />
                  </div>
                  <div style={{ fontSize:8, color:"#5a4a30", marginTop:4, lineHeight:1.4, textAlign:"center" }}>
                    Scan to verify<br/>authenticity
                  </div>
                </div>

                <div style={{ textAlign:"center" }}>
                  <div style={{ width:58, height:58, borderRadius:"50%", border:"1px solid rgba(200,150,12,0.5)", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", margin:"0 auto", background:"rgba(200,150,12,0.05)" }}>
                    <img src="/makalu-icon.svg" alt="" style={{ width:26, height:26 }} />
                  </div>
                  <div style={{ fontSize:8, color:"#8A6000", marginTop:3, letterSpacing:"0.1em" }}>AUTHENTIC</div>
                </div>
              </div>

            </div>

            <div style={{ position:"absolute", bottom:0, left:0, right:0, height:3, background:"linear-gradient(90deg, transparent, #8A6000, #FFD700, #C8960C, #FFD700, #8A6000, transparent)" }} />
          </div>
        </div>

        <div className="text-center pb-3" style={{ fontSize:11, color:"#5a4a30" }}>
          Card size: 86 × 54 mm (Print Ready)
        </div>
      </div>
    </div>
  );
}
